/**
 * QR codes that open a site's menu on a guest's phone, and the menu they open.
 *
 * Two audiences. The back office makes, lists and stops a site's codes and
 * fetches each one's image for printing — behind sign-in, so the image comes
 * through `api.download` with the token rather than an `<img src>` that
 * cannot carry one. The guest's page reads the menu with no sign-in at all:
 * `useGuestMenu` sends no token worth the name and must never end a session
 * over its answer.
 *
 * Prices arrive as decimal strings, already what the till charges at that
 * site, each size priced whole. Nothing here adds, rounds or converts them.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ALLERGEN_LABELS, type Allergen } from './inventory';
import type { ApiError } from './client';
import { useApi } from './provider';

export interface MenuLink {
  id: string;
  location_id: string;
  label: string;
  /** The address the printed code opens. */
  url: string;
  created_at: string;
  created_by: string;
  /** Set once stopped; printed copies open nothing after that. */
  revoked_at: string | null;
  is_live: boolean;
}

export interface GuestSize {
  name: string;
  price: string;
}

export interface GuestDish {
  name: string;
  description: string | null;
  price: string;
  /** Each size priced whole. Empty for a dish sold one way. */
  sizes: GuestSize[];
  /**
   * Allergen codes, or null when the kitchen has not declared every
   * ingredient. Null is not "none": the page says to ask a member of staff.
   */
  allergens: string[] | null;
  off_today: boolean;
  /** A signed address of the dish's photo, or null for none. */
  photo: string | null;
}

export interface GuestCategory {
  name: string;
  description: string | null;
  dishes: GuestDish[];
  /** The categories inside this one ("Juices" under "Drinks"), one level only. */
  sections: GuestCategory[];
}

export interface GuestMenu {
  restaurant: string;
  site: string;
  currency: string;
  locale: string;
  categories: GuestCategory[];
  /**
   * The site's logo, served for this link only, or null. Same origin as the
   * menu; the address changes when the logo does.
   */
  logo_url: string | null;
  /** The site's chosen colour for its menu, `#rrggbb`, or null for the page's default. */
  accent: string | null;
}

const LINKS = ['menu-links'] as const;

/** A site's codes, live first. Stopped ones stay listed as the record of what was printed. */
export function useMenuLinks(locationId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...LINKS, locationId],
    queryFn: ({ signal }) =>
      api.get<MenuLink[]>(
        `/v1/menu/links?location_id=${encodeURIComponent(locationId ?? '')}`,
        signal,
      ),
    enabled: locationId !== null,
  });
}

export function useMakeMenuLink() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<MenuLink, ApiError, { locationId: string; label: string }>({
    mutationFn: ({ locationId, label }) =>
      api.post<MenuLink>('/v1/menu/links', { location_id: locationId, label }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: LINKS });
    },
  });
}

/** For good: there is no starting a stopped code again. Make a new one instead. */
export function useStopMenuLink() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<MenuLink, ApiError, { linkId: string }>({
    mutationFn: ({ linkId }) => api.post<MenuLink>(`/v1/menu/links/${linkId}/stop`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: LINKS });
    },
  });
}

/** Where a code's print file is fetched from, for `useDownload`. */
export function menuLinkImagePath(linkId: string, kind: 'svg' | 'png'): string {
  return `/v1/menu/links/${linkId}/qr.${kind}`;
}

/**
 * The code's SVG as an object URL an `<img>` can show, released when the
 * component goes. Fetched with the token, like a download, because the image
 * route is behind sign-in.
 */
export function useMenuLinkImage(linkId: string): {
  src: string | null;
  error: ApiError | null;
} {
  const api = useApi();
  const image = useQuery<Blob, ApiError>({
    queryKey: [...LINKS, 'image', linkId],
    queryFn: async () => (await api.download(menuLinkImagePath(linkId, 'svg'))).blob,
    staleTime: Infinity,
  });
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!image.data) return undefined;
    const url = URL.createObjectURL(image.data);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [image.data]);
  return { src, error: image.error };
}

/**
 * The menu a printed code opens. No sign-in; a 404 is the answer for a code
 * that was stopped or never existed, and is never taken as the end of a
 * session — there is none.
 */
export function useGuestMenu(token: string) {
  const api = useApi();
  return useQuery<GuestMenu, ApiError>({
    queryKey: ['guest-menu', token],
    queryFn: ({ signal }) =>
      api.request<GuestMenu>(`/v1/public/menu/${encodeURIComponent(token)}`, {
        signal,
        unauthenticatedEndsSession: false,
      }),
    // A guest keeps the page open through a meal; a dish 86'd meanwhile
    // should read as off when they look again, without a reload.
    refetchInterval: 120_000,
  });
}

/** An allergen code in words a guest reads: "Cereals with gluten", not the code. */
export function allergenWords(code: string): string {
  return ALLERGEN_LABELS[code as Allergen] ?? code.replace(/_/g, ' ');
}

/**
 * The words for a dish's allergens on the guest page, or null to say "ask".
 * An empty declared list is a real answer — somebody looked and found none —
 * and reads differently from a list nobody finished.
 */
export function describeDishAllergens(allergens: string[] | null): string | null {
  if (allergens === null) return null;
  if (allergens.length === 0) return 'No named allergens';
  return `Contains: ${allergens.map(allergenWords).join(', ')}`;
}
